/* =====================================================================
   DIGITAL TWIN — CANVAS RENDERER (drawing only, no calculations)

   Draws one sewing line as a U-shaped row of workstations on a factory
   floor, seen at an angle. Built on the projection, drag-to-turn and
   pause-when-hidden behaviour of the earlier platform model.

     createTwinRenderer(canvas, { onSelect })
       .setModel(view)   view = { stations[], rate, bottleneckId, labels }
       .setSelected(id)
       .destroy()

   It receives already-calculated numbers (src/twin/calc.js) and never
   changes them. Pieces flowing along the line move at a speed taken from
   the line rate, so a faster line after balancing visibly flows faster.
   Status is never colour-only: each station also shows a symbol, and the
   accessible station list next to the canvas carries the same information.
   ===================================================================== */

export const TWIN_COLORS = {
  floor: "#0C2638", grid: "rgba(157,178,189,.10)", aisle: "rgba(157,178,189,.18)",
  table: "#1B3B50", tableSide: "#132E40", head: "#DCE6EA", headSide: "#A9B9C1",
  running: "#16E6B2", idle: "#9DB2BD", stopped: "#FF7A6B", bottleneck: "#F2B84B",
  text: "#E8EFF2", muted: "#9DB2BD", piece: "#1EC8FF", select: "#FFFFFF",
};
const C = TWIN_COLORS;

const TILT = 0.62;   // camera pitch
const D = 9;         // perspective distance
const TH_MIN = -0.35, TH_MAX = 0.35; // drag range (radians) — keeps labels readable

export function createTwinRenderer(canvas, { onSelect } = {}) {
  const ctx = canvas.getContext("2d");
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let view = null, selected = null, layout = [];
  let W = 0, H = 0, DPR = 1, K = 1, CX = 0, CY = 0, theta = -0.18;
  let raf = 0, last = 0, visible = false, drag = null, moved = false;
  let pieces = [], spawnAcc = 0, pathLen = 1, path = [];

  /* ---- projection ---- */
  const raw = (x, y, z, th) => {
    const x1 = x * Math.cos(th) - z * Math.sin(th);
    const z1 = x * Math.sin(th) + z * Math.cos(th);
    const sy = y * Math.cos(TILT) + z1 * Math.sin(TILT);
    const depth = z1 * Math.cos(TILT) - y * Math.sin(TILT);
    const s = D / (D + depth);
    return [x1 * s, -sy * s, s, depth];
  };
  const P = (x, y, z) => { const r = raw(x, y, z, theta); return [CX + r[0] * K, CY + r[1] * K, r[2], r[3]]; };

  /* ---- U-shaped layout: back row left→right, front row right→left ---- */
  const buildLayout = () => {
    const n = view.stations.length, top = Math.ceil(n / 2), gap = 1.15;
    const half = ((top - 1) * gap) / 2;
    layout = view.stations.map((s, i) => {
      const back = i < top;
      const k = back ? i : n - 1 - i;
      return { s, x: -half + k * gap, z: back ? -0.95 : 0.95, back };
    });
    // flow path: along each station's aisle edge, turning at the right end
    path = layout.map((l) => [l.x, l.back ? -0.35 : 0.35]);
    pathLen = 0;
    for (let i = 1; i < path.length; i++) pathLen += Math.hypot(path[i][0] - path[i - 1][0], path[i][1] - path[i - 1][1]);
    fit();
  };
  const bounds = () => { const xs = layout.map((l) => l.x); return { x0: Math.min(...xs) - 1.35, x1: Math.max(...xs) + 0.65, z0: -1.5, z1: 1.5 }; };

  const fit = () => {
    if (!layout.length || !W) return;
    const b = bounds();
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (let a = 0; a <= 8; a++) {
      const th = TH_MIN + ((TH_MAX - TH_MIN) * a) / 8;
      for (const [x, z] of [[b.x0, b.z0], [b.x1, b.z0], [b.x1, b.z1], [b.x0, b.z1]]) for (const y of [0, 0.9]) {
        const r = raw(x, y, z, th);
        minX = Math.min(minX, r[0]); maxX = Math.max(maxX, r[0]); minY = Math.min(minY, r[1]); maxY = Math.max(maxY, r[1]);
      }
    }
    const pad = 14 * DPR, padTop = 30 * DPR;
    K = Math.min((W - pad * 2) / (maxX - minX), (H - padTop - pad) / (maxY - minY));
    CX = pad + (W - pad * 2 - (maxX - minX) * K) / 2 - minX * K;
    CY = padTop + (H - padTop - pad - (maxY - minY) * K) / 2 - minY * K;
  };

  const size = () => {
    const r = canvas.getBoundingClientRect();
    DPR = Math.min(2, window.devicePixelRatio || 1);
    W = canvas.width = Math.max(1, Math.round(r.width * DPR));
    H = canvas.height = Math.max(1, Math.round(r.height * DPR));
    fit();
    draw();
  };

  /* ---- drawing helpers ---- */
  const poly = (pts, fill, stroke, lw = 1, dash) => {
    ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.closePath();
    if (fill) { ctx.fillStyle = fill; ctx.fill(); }
    if (stroke) { ctx.setLineDash(dash || []); ctx.strokeStyle = stroke; ctx.lineWidth = lw * DPR; ctx.stroke(); ctx.setLineDash([]); }
  };
  const box = (cx, cz, hw, hd, y0, y1, top, side, stroke) => {
    const b = [P(cx - hw, y0, cz - hd), P(cx + hw, y0, cz - hd), P(cx + hw, y0, cz + hd), P(cx - hw, y0, cz + hd)];
    const t = [P(cx - hw, y1, cz - hd), P(cx + hw, y1, cz - hd), P(cx + hw, y1, cz + hd), P(cx - hw, y1, cz + hd)];
    const faces = [0, 1, 2, 3].map((i) => { const j = (i + 1) % 4; return { pts: [b[i], b[j], t[j], t[i]], d: (b[i][3] + b[j][3]) / 2 }; });
    faces.sort((a, c) => c.d - a.d).forEach((f) => poly(f.pts, side, stroke ? `${stroke}` : null, 0.6));
    poly(t, top, stroke || null, 0.8);
    return t;
  };
  const text = (str, x, y, { color = C.text, size = 11, weight = 600, align = "center", bg } = {}) => {
    ctx.font = `${weight} ${size * DPR}px "IBM Plex Sans", "Noto Sans KR", "Noto Sans CJK KR", "Malgun Gothic", system-ui, sans-serif`;
    ctx.textAlign = align; ctx.textBaseline = "middle";
    if (bg) {
      const w = ctx.measureText(str).width + 10 * DPR, h = (size + 8) * DPR;
      const x0 = align === "center" ? x - w / 2 : align === "left" ? x - 5 * DPR : x - w + 5 * DPR;
      ctx.fillStyle = bg; ctx.beginPath(); ctx.roundRect ? ctx.roundRect(x0, y - h / 2, w, h, 3 * DPR) : ctx.rect(x0, y - h / 2, w, h); ctx.fill();
    }
    ctx.fillStyle = color; ctx.fillText(str, x, y);
  };
  const glyph = (status, x, y, r) => {
    ctx.fillStyle = C[status]; ctx.strokeStyle = C[status]; ctx.lineWidth = 2 * DPR;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fillStyle = "rgba(8,28,44,.92)"; ctx.fill(); ctx.stroke();
    ctx.fillStyle = C[status];
    if (status === "running") { ctx.beginPath(); ctx.moveTo(x - r * 0.3, y - r * 0.45); ctx.lineTo(x + r * 0.5, y); ctx.lineTo(x - r * 0.3, y + r * 0.45); ctx.closePath(); ctx.fill(); }
    else if (status === "idle") { ctx.fillRect(x - r * 0.4, y - r * 0.4, r * 0.25, r * 0.8); ctx.fillRect(x + r * 0.15, y - r * 0.4, r * 0.25, r * 0.8); }
    else { ctx.fillRect(x - r * 0.38, y - r * 0.38, r * 0.76, r * 0.76); }
  };

  const pointAt = (u) => { // u in [0, pathLen]
    let acc = 0;
    for (let i = 1; i < path.length; i++) {
      const seg = Math.hypot(path[i][0] - path[i - 1][0], path[i][1] - path[i - 1][1]);
      if (acc + seg >= u) { const f = seg ? (u - acc) / seg : 0; return [path[i - 1][0] + (path[i][0] - path[i - 1][0]) * f, path[i - 1][1] + (path[i][1] - path[i - 1][1]) * f]; }
      acc += seg;
    }
    return path[path.length - 1];
  };

  function draw() {
    if (!W || !view) return;
    ctx.clearRect(0, 0, W, H);
    const b = bounds();
    // floor and grid
    poly([P(b.x0, 0, b.z0), P(b.x1, 0, b.z0), P(b.x1, 0, b.z1), P(b.x0, 0, b.z1)], C.floor, C.grid);
    for (let x = Math.ceil(b.x0); x <= b.x1; x += 1) { const a = P(x, 0, b.z0), c = P(x, 0, b.z1); ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(c[0], c[1]); ctx.strokeStyle = C.grid; ctx.lineWidth = DPR; ctx.stroke(); }
    // aisle (the flow path)
    ctx.beginPath(); path.forEach(([x, z], i) => { const p = P(x, 0, z); i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]); });
    ctx.setLineDash([6 * DPR, 6 * DPR]); ctx.strokeStyle = C.aisle; ctx.lineWidth = 1.5 * DPR; ctx.stroke(); ctx.setLineDash([]);

    // stations, far to near
    const order = [...layout].sort((a, c) => P(c.x, 0, c.z)[3] - P(a.x, 0, a.z)[3]);
    order.forEach((l) => {
      const s = l.s, isSel = s.id === selected, isB = s.id === view.bottleneckId;
      // WIP waiting in front of this station: one slab per bundle
      const bundles = Math.min(4, Math.ceil(s.wipBefore / (view.bundleSize || 24)));
      for (let k = 0; k < bundles; k++) box(l.x - 0.42, l.back ? -0.42 : 0.42, 0.09, 0.07, k * 0.05, k * 0.05 + 0.04, "rgba(30,200,255,.55)", "rgba(30,200,255,.3)");
      // table + machine head
      const stroke = isSel ? C.select : isB ? C.bottleneck : null;
      const top = box(l.x, l.z, 0.42, 0.3, 0, 0.32, C.table, C.tableSide, stroke);
      box(l.x + 0.05, l.z + (l.back ? 0.02 : -0.02), 0.16, 0.08, 0.32, 0.46, C.head, C.headSide);
      if (isB || isSel) poly(top, null, stroke, isSel ? 2.4 : 2, isB && !isSel ? [5 * DPR, 4 * DPR] : null);
      // operators (one dot each) on the aisle side
      for (let k = 0; k < s.operators; k++) {
        const ox = l.x + (k - (s.operators - 1) / 2) * 0.22;
        const p = P(ox, 0.36, l.back ? -0.5 : 0.5);
        ctx.beginPath(); ctx.arc(p[0], p[1], 4.2 * DPR * p[2], 0, Math.PI * 2); ctx.fillStyle = "#C9D6DC"; ctx.fill();
      }
      // status glyph + short name
      const g = P(l.x, 0.95, l.z);
      glyph(s.status, g[0], g[1], 8 * DPR * g[2]);
      if (isB) {
        const t = P(l.x, 1.32, l.z);
        text(view.labels.bottleneck, t[0], t[1], { color: "#081C2C", size: 10.5, weight: 700, bg: C.bottleneck });
      }
    });

    // pieces flowing along the aisle
    pieces.forEach((pc) => {
      const [x, z] = pointAt(pc.u); const p = P(x, 0.05, z);
      ctx.beginPath(); ctx.arc(p[0], p[1], 2.6 * DPR * p[2], 0, Math.PI * 2); ctx.fillStyle = C.piece; ctx.fill();
    });

    // labels drawn last so stations never cover them: station numbers, line in/out
    layout.forEach((l) => {
      const g = P(l.x, 0.95, l.z);
      text(String(l.s.index + 1).padStart(2, "0"), g[0] + 13 * DPR * g[2], g[1], { color: l.s.id === selected ? C.select : C.muted, size: 10, weight: 600, align: "left" });
    });
    const s0 = P(path[0][0] - 1.15, 0, path[0][1] - 0.15), sEnd = P(path[path.length - 1][0] - 1.15, 0, path[path.length - 1][1] + 0.15);
    text(`${view.labels.in} →`, s0[0], s0[1], { color: C.text, size: 10, weight: 600, align: "right", bg: "rgba(8,28,44,.85)" });
    text(`← ${view.labels.out}`, sEnd[0], sEnd[1], { color: C.text, size: 10, weight: 600, align: "right", bg: "rgba(8,28,44,.85)" });

    // selected label (drawn last so it is never covered)
    const sl = layout.find((l) => l.s.id === selected);
    if (sl) {
      const t = P(sl.x, sl.s.id === view.bottleneckId ? 1.62 : 1.32, sl.z);
      text(sl.s.opLabel, t[0], t[1], { color: "#081C2C", size: 11, weight: 700, bg: C.select });
    }
  }

  function tick(ts) {
    const dt = Math.min(60, ts - (last || ts)); last = ts;
    // visual speed: a line making 60 pcs/h moves pieces at ~0.6 floor units/s
    const speed = (view.rate / 100) * 1.0;
    spawnAcc += dt;
    const every = 900 / Math.max(0.2, view.rate / 60);
    if (spawnAcc > every) { spawnAcc = 0; pieces.push({ u: 0 }); }
    pieces.forEach((p) => { p.u += (speed * dt) / 1000; });
    pieces = pieces.filter((p) => p.u < pathLen);
    draw();
    raf = visible && !document.hidden ? requestAnimationFrame(tick) : 0;
  }
  const start = () => { if (!raf && visible && !reduce && !document.hidden && view) { last = 0; raf = requestAnimationFrame(tick); } };

  const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) start(); });
  io.observe(canvas);
  const onVis = () => start();
  document.addEventListener("visibilitychange", onVis);

  /* ---- pointer: drag to turn, tap to select ---- */
  const hit = (e) => {
    const r = canvas.getBoundingClientRect();
    const x = (e.clientX - r.left) * DPR, y = (e.clientY - r.top) * DPR;
    let best = null, bd = 46 * DPR;
    layout.forEach((l) => { const p = P(l.x, 0.4, l.z); const d = Math.hypot(p[0] - x, p[1] - y); if (d < bd) { bd = d; best = l.s.id; } });
    return best;
  };
  const down = (e) => { drag = { x: e.clientX, th: theta }; moved = false; };
  const move = (e) => {
    if (drag) {
      const dx = e.clientX - drag.x;
      if (Math.abs(dx) > 4) { moved = true; canvas.setPointerCapture?.(e.pointerId); }
      if (moved) { theta = Math.max(TH_MIN, Math.min(TH_MAX, drag.th + dx * 0.006)); if (!raf) draw(); }
    } else canvas.style.cursor = hit(e) ? "pointer" : "grab";
  };
  const up = (e) => { if (drag && !moved) { const id = hit(e); if (id) onSelect?.(id); } drag = null; };
  canvas.addEventListener("pointerdown", down);
  canvas.addEventListener("pointermove", move);
  canvas.addEventListener("pointerup", up);
  canvas.addEventListener("pointercancel", () => { drag = null; });

  const ro = new ResizeObserver(size);
  ro.observe(canvas);

  return {
    setModel(v) {
      const relayout = !view || view.lineId !== v.lineId;
      view = { ...v, stations: v.stations.map((s, i) => ({ ...s, index: i })) };
      if (relayout) { pieces = []; buildLayout(); }
      else layout.forEach((l) => { l.s = view.stations.find((s) => s.id === l.s.id); });
      if (reduce && !pieces.length) for (let i = 0; i < 10; i++) pieces.push({ u: (pathLen * i) / 10 });
      if (!W) size(); else draw();
      start();
    },
    setSelected(id) { selected = id; draw(); },
    destroy() {
      cancelAnimationFrame(raf); io.disconnect(); ro.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      canvas.removeEventListener("pointerdown", down); canvas.removeEventListener("pointermove", move); canvas.removeEventListener("pointerup", up);
    },
  };
}
